/**
 * Where the app opens: Início (docs/refactor/09-inicio.md), with the prompt box — unless no agent is connected
 * yet, and then Agentes › Conectar agente, which is the first thing to do.
 *
 * Connected means, in the desktop app, an agent installed here that can run (Claude Code, Codex
 * CLI) and this person's Vephus key in the system's vault; in a browser, an agent key of theirs
 * that an agent has used. Only on opening: a page asked for by its address opens as asked.
 */

import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { isDesktop, vaultHas } from '../lib/desktop'
import { detectAgents } from '../lib/localAgent'
import { startPath } from '../lib/startPath'

/** In a browser: an agent key of this person's that an agent has used. */
async function webAgentConnected(): Promise<boolean> {
  const me = await api.auth.me()
  // Somebody with no developer of their own (a manager, a viewer) has no agent to connect.
  if (!me.developerId) return true
  const keys = await api.me.credentials()
  return keys.some((k) => !k.revokedAt && Boolean(k.lastUsedAt))
}

export default function Start() {
  const navigate = useNavigate()
  const [to, setTo] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    const go = (path: string) => alive && setTo(path)
    if (isDesktop()) {
      // The vault answers at once; finding the agents takes a few seconds. With a key, Início
      // opens right away and the agents are checked behind it — none that can run, and it moves on
      // to Conectar agente, unless the person already went somewhere else.
      vaultHas()
        .catch(() => false)
        .then((key) => {
          if (!key) return go(startPath(true, false))
          go(startPath(true, true))
          void detectAgents()
            .then((agents) => {
              if (!agents.some((agent) => agent.installed && agent.runnable) && window.location.pathname === startPath(true, true))
                navigate(startPath(true, false), { replace: true })
            })
            .catch(() => {})
        })
    } else {
      webAgentConnected()
        // Unsure (offline, an error): Início. Sending someone to set up what they have is worse.
        .catch(() => true)
        .then((connected) => go(startPath(false, connected)))
    }
    return () => {
      alive = false
    }
  }, [navigate])
  return to ? <Navigate to={to} replace /> : null
}
